import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SquareButton from '@/components/SquareButton';
import { postJson } from '@/lib/api';

const inputCls = 'mt-2 h-12 border-2 border-[hsl(var(--c-ink))] bg-white px-3 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[hsl(var(--c-mango-orange))]';
const labelCls = 'eyebrow text-[hsl(var(--c-ink))]/70';

const SERVICES = [
  { id: 'harvest', label: 'Harvest assistance' },
  { id: 'purchase', label: 'Fruit purchasing' },
  { id: 'maintenance', label: 'Tree maintenance referrals' },
  { id: 'cleanup', label: 'Tree cleanup' },
  { id: 'longterm', label: 'Long-term sourcing partnership' },
];
const TREE_COUNTS = ['1–2 trees', '3–5 trees', '6–10 trees', '11–25 trees', '25+ trees / orchard'];

export default function GrowerApplicationForm() {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', address: '', city: '',
    number_of_trees: '', tree_varieties: '', estimated_harvest: '',
    services_interested: [], notes: '', honeypot: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleService = (id) =>
    setForm((s) => ({
      ...s,
      services_interested: s.services_interested.includes(id) ? s.services_interested.filter((x) => x !== id) : [...s.services_interested, id],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Please share at least your name and email.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await postJson('/grower-applications', { ...form, source_page: 'growers' });
      setSubmitted(true);
      toast.success(res.message || 'Application received!');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="py-12 text-center" data-testid="grower-success">
        <div className="mx-auto inline-flex h-16 w-16 items-center justify-center bg-[hsl(var(--c-leaf))] border-2 border-[hsl(var(--c-ink))]">
          <CheckCircle2 className="h-8 w-8 text-white" />
        </div>
        <h3 className="mt-6 font-heading text-3xl sm:text-4xl font-extrabold">Thanks for reaching out!</h3>
        <p className="mt-3 text-[hsl(var(--c-ink-soft))] max-w-lg mx-auto">
          A team member will contact you to learn about your trees and walk you through harvest options and grower partnership opportunities.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-5" data-testid="grower-application-form">
      <input type="text" name="website" value={form.honeypot} onChange={(e) => update('honeypot', e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" />

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="g-name" className={labelCls}>Full Name *</Label>
          <Input id="g-name" data-testid="grower-name-input" required value={form.name} onChange={(e) => update('name', e.target.value)} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="g-email" className={labelCls}>Email *</Label>
          <Input id="g-email" type="email" data-testid="grower-email-input" required value={form.email} onChange={(e) => update('email', e.target.value)} className={inputCls} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="g-phone" className={labelCls}>Phone</Label>
          <Input id="g-phone" data-testid="grower-phone-input" value={form.phone} onChange={(e) => update('phone', e.target.value)} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="g-city" className={labelCls}>City</Label>
          <Input id="g-city" data-testid="grower-city-input" value={form.city} onChange={(e) => update('city', e.target.value)} className={inputCls} />
        </div>
      </div>

      <div>
        <Label htmlFor="g-address" className={labelCls}>Property Address (optional)</Label>
        <Input id="g-address" data-testid="grower-address-input" value={form.address} onChange={(e) => update('address', e.target.value)} className={inputCls} />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className={labelCls}>Number of Trees</Label>
          <Select value={form.number_of_trees} onValueChange={(v) => update('number_of_trees', v)}>
            <SelectTrigger data-testid="grower-trees-trigger" className={inputCls}><SelectValue placeholder="How many mango trees?" /></SelectTrigger>
            <SelectContent>
              {TREE_COUNTS.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="g-varieties" className={labelCls}>Known Varieties (optional)</Label>
          <Input id="g-varieties" data-testid="grower-varieties-input" value={form.tree_varieties} onChange={(e) => update('tree_varieties', e.target.value)} className={inputCls} placeholder="e.g. Tommy Atkins, Haden, unknown" />
        </div>
      </div>

      <div>
        <Label className={labelCls}>Services I’m interested in</Label>
        <div className="mt-2 grid sm:grid-cols-2 gap-0 border-2 border-[hsl(var(--c-ink))]">
          {SERVICES.map((s, i) => (
            <label key={s.id} className={`flex items-start gap-3 bg-white p-4 cursor-pointer hover:bg-[hsl(var(--c-cream-deep))] btn-transition ${i % 2 === 0 ? 'sm:border-r-2 sm:border-[hsl(var(--c-ink))]' : ''} ${i < SERVICES.length - (SERVICES.length % 2 || 2) ? 'border-b-2 border-[hsl(var(--c-ink))]' : ''}`}>
              <Checkbox checked={form.services_interested.includes(s.id)} onCheckedChange={() => toggleService(s.id)} data-testid={`grower-service-${s.id}`} className="mt-0.5" />
              <span className="text-sm font-semibold">{s.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <Label htmlFor="g-notes" className={labelCls}>Anything else we should know?</Label>
        <Textarea id="g-notes" data-testid="grower-notes-input" value={form.notes} onChange={(e) => update('notes', e.target.value)} className={`${inputCls} h-auto py-3`} rows={4} placeholder="Tree age, condition, harvest history, access notes, etc." />
      </div>

      <SquareButton type="submit" disabled={submitting} variant="leaf" size="lg" fullWidth testId="grower-form-submit-button">
        {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>) : 'Become a Grower Partner'}
      </SquareButton>
    </form>
  );
}
